import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileCheck2 } from "lucide-react";
import { Card } from "@shared/ui/containers/Card";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { pageCopy } from "@shared/lib/pageCopy";
import { fetchMyResponseDetail, type SubmissionAnswer } from "./api";
import { formTypeLabel, getSurveyRouteContext } from "./routeContext";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function SurveyResponseView(): JSX.Element {
  const location = useLocation();
  const { responseId = "" } = useParams<{ responseId: string }>();
  const copy = pageCopy().surveys;
  const route = getSurveyRouteContext(location.pathname);

  const query = useQuery({
    queryKey: ["survey-response", responseId],
    queryFn: () => fetchMyResponseDetail(responseId),
    enabled: !!responseId,
  });

  const groupedAnswers = useMemo(
    () => groupAnswers(query.data?.answers ?? []),
    [query.data?.answers],
  );
  const response = query.data;
  const formLabel = formTypeLabel(response?.flow_type, route.isEformRoute);
  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={response?.name ?? copy.title}
      subtitle={
        response
          ? responseSubtitle(response.submitted_at, formLabel)
          : copy.subtitle
      }
      backTo={route.listRoute}
      backLabel={route.detailBackLabel}
      backBehavior="parent"
    />
  );

  if (query.isLoading) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          <ErrorState onRetry={() => query.refetch()} />
        </div>
      </div>
    );
  }

  const responseData = query.data;
  const resolvedFormLabel = formTypeLabel(responseData.flow_type, route.isEformRoute);

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={responseData.name}
        subtitle={responseSubtitle(
          responseData.submitted_at,
          resolvedFormLabel,
        )}
        backTo={route.listRoute}
        backLabel={route.detailBackLabel}
        backBehavior="parent"
      />

      <div className="px-5 pt-5 pb-safe-or-6">
        <Card noGrain className="p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-success/15 text-success">
              <FileCheck2 className="h-6 w-6" strokeWidth={1.5} />
            </span>
            <div className="min-w-0">
              <span className="inline-flex rounded-pill bg-success/15 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-success">
                {resolvedFormLabel}
              </span>
              <p className="mt-3 text-body text-ink-secondary">
                {responseData.submitted_at
                  ? "Your submitted answers are shown below."
                  : "This response is still in progress and can continue in the form flow."}
              </p>
            </div>
          </div>
        </Card>

        {groupedAnswers.length === 0 ? (
          <div className="mt-4">
            <EmptyState
              icon={<FileCheck2 className="h-7 w-7" strokeWidth={1.5} />}
              title="No captured answers."
              description="This response exists, but there are no visible answers to show yet."
            />
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            {groupedAnswers.map((group) => (
              <Card key={group.title} noGrain>
                <p className="mb-3 font-display text-headline text-ink">
                  {group.title}
                </p>
                <div className="flex flex-col divide-y divide-hairline">
                  {group.answers.map((answer, index) => (
                    <div
                      key={`${answer.content_key ?? answer.item_key ?? index}`}
                      className="py-3 first:pt-0 last:pb-0"
                    >
                      <p className="text-caption uppercase tracking-[0.14em] text-ink-muted">
                        {answer.name ??
                          answer.item_key ??
                          answer.content_key ??
                          `Answer ${index + 1}`}
                      </p>
                      <p className="mt-1 whitespace-pre-line text-body text-ink">
                        {formatAnswer(answer.item_value)}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function responseSubtitle(
  submittedAt: string | null | undefined,
  formLabel: string,
): string {
  if (!submittedAt) return `${formLabel} response in progress`;
  return `${formLabel} submitted ${DATE_FMT.format(new Date(submittedAt))}`;
}

function groupAnswers(
  answers: SubmissionAnswer[],
): Array<{ title: string; answers: SubmissionAnswer[] }> {
  const sorted = [...answers].sort((a, b) => {
    const stepDelta = (a.step_sequence ?? 0) - (b.step_sequence ?? 0);
    if (stepDelta !== 0) return stepDelta;
    return (a.sequence_number ?? 0) - (b.sequence_number ?? 0);
  });

  const groups = new Map<string, SubmissionAnswer[]>();
  sorted.forEach((answer) => {
    const title = answer.step_name || "Submitted answers";
    groups.set(title, [...(groups.get(title) ?? []), answer]);
  });

  return Array.from(groups.entries()).map(([title, group]) => ({
    title,
    answers: group,
  }));
}

function formatAnswer(value: unknown): string {
  if (value === null || value === undefined || value === "")
    return "Not answered";
  if (Array.isArray(value)) return value.map(formatAnswer).join(", ");
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}
